import { CheckOutlined, DownOutlined, SearchOutlined } from '@ant-design/icons';
import { useDebouncedState } from '@react-hookz/web';
import {
  useQuery,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import { Input, Popover } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { PaginatedProfileList, Profile } from 'src/api-sdk';
import { ProfilesApi } from 'src/api-sdk';
import { Loader } from 'src/components/Loader';
import { brand } from 'src/styles/colors';
import { QUERIES } from 'src/util/globals';
import { useAPI } from 'src/util/use-api';
import { useAuth } from 'src/util/use-auth';

import { ProfileTile } from './ProfileTile';

interface ProfileSwitcherProps {
  /** Controlled so the account menu can open the same popover. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const labelStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: '#595959',
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
  maxWidth: 180,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

/**
 * Persistent indicator of the active profile in the topbar (BRD-3303).
 *
 * Single-profile customers get a quiet static label: their profile identity is
 * already carried by the white-labelled topbar logo and nav strip colour.
 * Business Radar administrators (`multiple_profiles`) get a switcher popover.
 */
export const ProfileSwitcher = ({
  open,
  onOpenChange,
}: ProfileSwitcherProps) => {
  const { t } = useTranslation('menu');
  const { user } = useAuth();
  const { api, configuration } = useAPI();
  const queryClient = useQueryClient();

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useDebouncedState('', 300);
  const [switchingId, setSwitchingId] = useState<string | null>(null);

  const active = user?.active_profile;
  const canSwitch = Boolean(user?.multiple_profiles);

  // Reset the search whenever the popover closes so it reopens clean.
  useEffect(() => {
    if (!open) {
      setQuery('');
      setDebouncedQuery('');
    }
  }, [open, setDebouncedQuery]);

  const getProfiles = useCallback(
    () =>
      new ProfilesApi(configuration, undefined, api)
        .profilesList({ query: debouncedQuery || undefined })
        .then((response) => response?.data),
    [api, configuration, debouncedQuery]
  );

  const { data: profiles, isFetching } = useQuery({
    queryKey: [QUERIES.PROFILES, debouncedQuery],
    queryFn: getProfiles,
    enabled: canSwitch && open,
    placeholderData: keepPreviousData,
  });

  const handleSelect = (profile: Profile) => {
    if (switchingId || profile.external_id === active?.external_id) {
      onOpenChange(false);
      return;
    }

    setSwitchingId(profile.external_id);
    new ProfilesApi(configuration, undefined, api)
      .profilesUserDetailsPartialUpdate({
        patchedUserUpdateRequest: { active_profile: profile.external_id },
      })
      .then(() => queryClient.invalidateQueries())
      .catch((error) => console.error('Failed to switch profile:', error))
      .finally(() => {
        setSwitchingId(null);
        onOpenChange(false);
      });
  };

  const indicator = (
    <>
      <ProfileTile
        name={active?.name}
        logo={active?.logo}
        color={active?.primary_color}
      />
      <span style={labelStyle} title={active?.name}>
        {active?.name}
      </span>
    </>
  );

  if (!canSwitch) {
    return (
      <div
        data-cy="profile-indicator"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '5px 8px 5px 0',
          cursor: 'default',
        }}
      >
        {indicator}
      </div>
    );
  }

  const results = profiles?.results || [];

  const content = (
    <div style={{ width: 320, padding: '8px 0 6px' }}>
      <div
        style={{
          padding: '10px 18px 8px',
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.8px',
          textTransform: 'uppercase',
          color: '#8c8c8c',
        }}
      >
        {t('Switch Profile')}
      </div>
      <div style={{ margin: '0 14px 8px' }}>
        <Input
          autoFocus
          allowClear
          value={query}
          placeholder={t('Search profiles')}
          prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
          onChange={(event) => {
            setQuery(event.target.value);
            setDebouncedQuery(event.target.value);
          }}
          style={{ height: 36, borderRadius: 8, fontSize: 14 }}
        />
      </div>
      <Loader loading={isFetching && results.length === 0}>
        <div style={{ maxHeight: 320, overflowY: 'auto' }}>
          {results.map((profile) => {
            const isActive = profile.external_id === active?.external_id;
            return (
              <div
                key={profile.external_id}
                role="button"
                tabIndex={0}
                data-cy="profile-option"
                onClick={() => handleSelect(profile)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleSelect(profile);
                  }
                }}
                onMouseEnter={(event) => {
                  event.currentTarget.style.background = '#f5fafd';
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.background = 'transparent';
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 11,
                  padding: '9px 18px',
                  cursor: switchingId ? 'progress' : 'pointer',
                  opacity:
                    switchingId && switchingId !== profile.external_id
                      ? 0.5
                      : 1,
                }}
              >
                <ProfileTile
                  name={profile.name}
                  logo={profile.logo}
                  color={profile.primary_color}
                  size={26}
                />
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: isActive ? 700 : 500,
                    color: isActive ? brand.primary : '#262626',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {profile.name}
                </span>
                {isActive && (
                  <CheckOutlined
                    style={{
                      marginLeft: 'auto',
                      color: brand.primary,
                      flex: 'none',
                    }}
                  />
                )}
              </div>
            );
          })}
          {!isFetching && results.length === 0 && (
            <div
              style={{ padding: '16px 18px', fontSize: 14, color: '#8c8c8c' }}
            >
              {t('No profiles found')}
            </div>
          )}
        </div>
      </Loader>
    </div>
  );

  return (
    <Popover
      open={open}
      onOpenChange={onOpenChange}
      trigger={['click']}
      placement="bottomRight"
      arrow={false}
      content={content}
      overlayInnerStyle={{ padding: 0, borderRadius: 12 }}
    >
      <button
        type="button"
        data-cy="profile-indicator"
        aria-haspopup="dialog"
        aria-expanded={open}
        title={t('Switch Profile')}
        onMouseEnter={(event) => {
          event.currentTarget.style.background = '#f5f7fa';
        }}
        onMouseLeave={(event) => {
          event.currentTarget.style.background = 'none';
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: 'none',
          border: 0,
          borderRadius: 8,
          padding: '5px 8px',
          cursor: 'pointer',
          fontFamily: 'inherit',
          textAlign: 'left',
        }}
      >
        {indicator}
        <DownOutlined
          style={{
            fontSize: 10,
            color: '#bfbfbf',
            transition: 'transform 200ms ease',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </button>
    </Popover>
  );
};

export default ProfileSwitcher;
